import { Col, Row } from 'antd';
import {
  ApplicantPreviewBlock,
  BillingPreviewBlock,
  CaseInfoPreviewBlock,
  ComplementingFactsPreviewBlock,
  DateAndTimePreviewBlock,
  EntrepreneurPreviewBlock,
  InspectionPreviewBlock,
  InvoicePreviewBlock,
  MotivationPreviewBlock,
  PaddingBlock,
  PreviewBlock,
  PurposePreviewBlock,
  RecoveryPreviewBlock
} from 'lib/Antd/Preview';
import { types } from 'mobx-state-tree';
import styled from 'styled-components';
import { CaseStatus, Panel, Step } from 'utils/enumerations';
import {
  diggingPermitPanelOrderList,
  diggingPermitStepPanels,
  dpPanelWithKeys,
  panelHasAnyValue
} from 'utils/panelUtils';
import { ApplicationBaseModel } from './applicationBaseModel';
import { CaseInfoModel, DepthAndWorkMethodBlockModel, EntrepreneurBlockModel } from './blocks';

const CompletePage = styled.div`
  height: 22.3cm;
`;
//22.3 actual paper height excluding the paper margin and the header margin

const DiggingPermitSpecificModel = types
  .model('DiggingPermitSpecificModel', {
    caseInfo: types.optional(CaseInfoModel, {}),
    entrepreneur: types.optional(EntrepreneurBlockModel, {}),
    executedIns: types.maybe(types.array(types.integer)),
    workMethodAndDepth: types.optional(DepthAndWorkMethodBlockModel, {}),
    locationDescription: types.maybe(types.string),
    approvalStatement: types.maybe(types.string),
    prolongedTo: types.maybe(types.string),
    newEndTime: types.maybe(types.string),
    prolongedStatus: types.maybe(types.integer),
    prolongedApprovedDate: types.maybe(types.string),
    workDone: types.maybe(types.string),
    inspectionDate: types.maybe(types.string),
    recoveryInspectionDate: types.maybe(types.string),
    recoveryInspectionNeeded: types.maybe(types.boolean),
    sendRecoveryInvoice: types.maybe(types.boolean),
    recoveryInvoiceDate: types.maybe(types.string),
    statusMotivation: types.maybe(types.string)
  })
  .views(self => ({
    get name() {
      return self.caseInfo.caseName ? self.caseInfo.caseName : self.purpose.purpose;
    }
  }));

export const DiggingPermitModel = types
  .compose(ApplicationBaseModel, DiggingPermitSpecificModel)
  .named('DiggingPermitModel')
  .views(self => ({
    getPreview(t, domains) {
      return (
        <>
          {self.status >= CaseStatus.Processed && (
            <CompletePage>
              <CaseInfoPreviewBlock
                caseInfo={self.caseInfo}
                caseStatus={self.status}
                currentPanel={self.currentPanel}
                prolongedStatus={self.prolongedStatus}
              />
              <MotivationPreviewBlock
                answer={self.statusMotivation ? self.statusMotivation : self.approvalStatement}
                handlingOfficerName={self.handlingOfficerName}
              />
              <InspectionPreviewBlock inspectionDate={self.inspectionDate} location={self.caseInfo.location} />
              <RecoveryPreviewBlock
                recoveryInspectionDate={self.recoveryInspectionDate}
                recoveryInspectionNeeded={self.recoveryInspectionNeeded}
              />
              <InvoicePreviewBlock
                recoveryInvoiceDate={self.recoveryInvoiceDate}
                sendRecoveryInvoice={self.sendRecoveryInvoice}
              />
            </CompletePage>
          )}
          <CaseInfoPreviewBlock
            caseInfo={self.caseInfo}
            caseStatus={self.status}
            currentPanel={self.currentPanel}
            prolongedStatus={self.prolongedStatus}
          />
          {self.isPanelTouchedOrHasValue(Panel.Questioneer) && (
            <ApplicantPreviewBlock
              applicant={self.applicant}
              billingInfo={self.billingInformation}
              currentPanel={self.currentPanel}
            />
          )}
          {self.isPanelTouchedOrHasValue(Panel.InvoiceAddress) && (
            <BillingPreviewBlock
              billingInfo={self.billingInformation}
              applicant={self.applicant}
              currentPanel={self.currentPanel}
            />
          )}
          {self.isPanelTouchedOrHasValue(Panel.EntrepreneurAndSupervisor) && (
            <EntrepreneurPreviewBlock entrepreneur={self.entrepreneur} currentPanel={self.currentPanel} />
          )}
          {self.isPanelTouchedOrHasValue(Panel.DateAndTimePeriod) && (
            <DateAndTimePreviewBlock
              dateTimeInfo={self.dateTimeInformation}
              prolongedTo={self.prolongedTo}
              prolongedStatus={self.prolongedStatus}
              currentPanel={self.currentPanel}
            />
          )}
          {self.isPanelTouchedOrHasValue(Panel.Purpose) && (
            <PurposePreviewBlock
              purpose={self.purpose}
              domains={domains}
              executedIns={self.executedIns}
              workMethodAndDepth={self.workMethodAndDepth}
              currentPanel={self.currentPanel}
            />
          )}
          {self.childCases.length > 0 || self.isPanelTouchedOrHasValue(Panel.AdditionalInformation) ? (
            <ComplementingFactsPreviewBlock
              additionalInformation={self.additionalInformation}
              attachments={self.attachments}
              childCases={self.childCases}
              currentPanel={self.currentPanel}
            />
          ) : null}
          {self.geometrySnapshot && (
            <CompletePage isMapPage>
              <PreviewBlock step={Step.Map} panel={Panel.Extent}>
                <Col span={24} offset={0}>
                  <PaddingBlock />
                  <Row justify="start">
                    <img
                      style={{ width: '100%' }}
                      className="preview-map"
                      src={self.geometrySnapshot}
                      alt={t('preview:extent')}
                    />
                  </Row>
                </Col>
              </PreviewBlock>
            </CompletePage>
          )}
        </>
      );
    },
    getPanelByKey: itemKey => {
      return dpPanelWithKeys.find(panel => panel.keys.find(panelKey => panelKey === itemKey))?.panelName;
    }
  }))
  .actions(self => ({
    setProlong: (prolongedTo, newEndDate, prolongedStatus) => {
      self.prolongedTo = prolongedTo;
      self.newEndTime = newEndDate;
      self.prolongedStatus = prolongedStatus;
    },
    isPanelTouchedOrHasValue: panel => {
      if (self.status >= CaseStatus.Registered) {
        self.setTouchedPanelIndex(panel);
        return true;
      }
      const currentPanelIndex = diggingPermitPanelOrderList.findIndex(touchedPanel => touchedPanel === panel);
      if (currentPanelIndex <= self.touchedPanelIndex) {
        return true;
      } else if (panelHasAnyValue(self.toJSON(), panel, dpPanelWithKeys)) {
        self.setTouchedPanelIndex(panel);
        return true;
      }
      return false;
    },
    setTouchedPanelIndex: panel => {
      const currentPanelIndex = diggingPermitPanelOrderList.findIndex(touchedPanel => touchedPanel === panel);
      if (self.touchedPanelIndex < currentPanelIndex) {
        self.touchedPanelIndex = currentPanelIndex;
        const step = diggingPermitStepPanels.find(steps => steps.panels.find(panelName => panel === panelName))?.step;
        self.setTouchedStep(step);
      }
    }
  }));
